(() => {
  if (window.__athomeSearchApiLoaded) return;
  window.__athomeSearchApiLoaded = true;

  const config = window.AthHomeSearchApiConfig || {};
  const endpoint = config.endpoint || 'https://searchserverapi1.com/getwidgets';
  const searchUrl = config.searchUrl || '/search';
  const apiKey = config.apiKey || '';
  const currency = config.currency || 'USD';
  const locale = config.locale || 'en-US';
  const headerSearchInputSelector = '.ath-site-header__search-input';
  let jsonpIndex = 0;

  const debounce = (callback, wait = 240) => {
    let timer = 0;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };

  const normalizeText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const fallbackBrands = [
    '3M',
    'Attends',
    'Bard Medical',
    'Cardinal Health',
    'Coloplast',
    'Convatec',
    'Cure Medical',
    'Dale',
    'First Quality',
    'Hollister',
    'HR HealthCare',
    'McKesson',
    'Medline',
    'Prevail',
    'Seni',
    'Teleflex-Rusch',
    'Tena',
    'Tranquility',
    'Urocare',
    'Wellspect',
  ];
  const searchableBrands = [...new Set([
    ...(Array.isArray(config.brands) ? config.brands : []),
    ...fallbackBrands,
  ].map((brand) => String(brand).trim()).filter(Boolean))];

  const editDistance = (left = '', right = '') => {
    const previous = Array.from({ length: right.length + 1 }, (_value, index) => index);
    for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
      const current = [leftIndex];
      for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
        current[rightIndex] = Math.min(
          current[rightIndex - 1] + 1,
          previous[rightIndex] + 1,
          previous[rightIndex - 1] + Number(left[leftIndex - 1] !== right[rightIndex - 1])
        );
      }
      previous.splice(0, previous.length, ...current);
    }
    return previous[right.length];
  };

  const findBrandCorrection = (query = '') => {
    const queryWords = normalizeText(query).split(' ').filter(Boolean);
    if (!queryWords.length) return '';

    let closest = null;
    searchableBrands.forEach((brand) => {
      const brandWords = normalizeText(brand).split(' ').filter(Boolean);
      const compactBrand = brandWords.join('');
      if (compactBrand.length < 5) return;

      const allowedDistance = compactBrand.length >= 12 ? 3 : compactBrand.length >= 8 ? 2 : 1;
      const maxSpanLength = Math.min(queryWords.length, brandWords.length + 1);
      for (let start = 0; start < queryWords.length; start += 1) {
        for (let spanLength = 1; spanLength <= maxSpanLength && start + spanLength <= queryWords.length; spanLength += 1) {
          const compactSpan = queryWords.slice(start, start + spanLength).join('');
          if (Math.abs(compactSpan.length - compactBrand.length) > allowedDistance) continue;

          const distance = editDistance(compactSpan, compactBrand);
          if (distance === 0 || distance > allowedDistance) continue;
          const candidate = { brand, distance, start, spanLength, compactLength: compactSpan.length };
          if (
            !closest
            || distance < closest.distance
            || (distance === closest.distance && compactSpan.length > closest.compactLength)
          ) closest = candidate;
        }
      }
    });

    if (!closest) return '';
    return [
      ...queryWords.slice(0, closest.start),
      closest.brand,
      ...queryWords.slice(closest.start + closest.spanLength),
    ].join(' ');
  };

  const normalizeLink = (link = '') => {
    if (!link) return searchUrl;
    if (link.startsWith('//')) return `https:${link}`;
    if (link.startsWith('/')) return `${window.location.origin}${link}`;
    return link.replace(/^http:\/\//, 'https://');
  };

  const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const formatResultCount = (value) => {
    const count = Number(value) || 0;
    return `${count} result${count === 1 ? '' : 's'}`;
  };

  const rankItems = (items = [], query = '', correctedQuery = '') => {
    const normalizedQuery = normalizeText(correctedQuery || query);
    const compactQuery = normalizedQuery.replace(/ /g, '');
    const queryTokens = normalizedQuery.split(' ').filter((token) => token.length > 1);

    return items
      .map((item, originalIndex) => {
        const title = normalizeText(item.title);
        const productCode = normalizeText(item.product_code).replace(/ /g, '');
        const matchedTokens = queryTokens.filter((token) => title.includes(token)).length;
        let score = matchedTokens * 500;

        if (queryTokens.length && matchedTokens === queryTokens.length) score += 1500;
        if (title === normalizedQuery) score += 6000;
        else if (title.includes(normalizedQuery)) score += 4000;

        if (productCode === compactQuery) score += 10000;
        else if (productCode.startsWith(compactQuery)) score += 8000;
        else if (compactQuery.length >= 3 && productCode.includes(compactQuery)) score += 7000;

        return { item, originalIndex, score };
      })
      .sort((a, b) => b.score - a.score || a.originalIndex - b.originalIndex)
      .map(({ item }) => item);
  };

  const requestSearch = (parameters) => new Promise((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('Search API key is missing'));
      return;
    }

    const callbackName = `__athomeSearchApiCallback${Date.now()}${++jsonpIndex}`;
    const url = new URL(endpoint);
    const script = document.createElement('script');
    let finished = false;

    const cleanup = () => {
      delete window[callbackName];
      script.remove();
    };

    const timer = window.setTimeout(() => {
      if (finished) return;
      finished = true;
      cleanup();
      reject(new Error('Search request timed out'));
    }, 9000);

    window[callbackName] = (data) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      cleanup();
      if (data?.error) reject(new Error(data.error));
      else resolve(data || { totalItems: 0, items: [] });
    };

    const searchParameters = {
      apiKey,
      output: 'jsonp',
      callback: callbackName,
      suggestions: 'true',
      queryCorrection: 'true',
      ...parameters,
    };

    Object.entries(searchParameters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) url.searchParams.set(key, String(value));
    });

    script.async = true;
    script.src = url.toString();
    script.onerror = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      cleanup();
      reject(new Error('Search request failed'));
    };
    document.head.appendChild(script);
  });

  const requestSearchWithBrandCorrection = async (parameters) => {
    const response = await requestSearch(parameters);
    if ((response?.items || []).length || !parameters?.q) return response;

    const correctedQuery = findBrandCorrection(parameters.q);
    if (!correctedQuery) return response;
    const correctedResponse = await requestSearch({ ...parameters, q: correctedQuery });
    if (!(correctedResponse?.items || []).length) return response;
    return { ...correctedResponse, correctedQuery: correctedResponse.correctedQuery || correctedQuery };
  };

  const makeElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const preserveHeaderInputValue = (input, query) => {
    if (!input || !query) return;
    const form = input.closest('form');
    if (!form) return;
    form.__athApiExpectedQuery = query;

    const restore = () => {
      const currentInput = form.querySelector(headerSearchInputSelector);
      if (form.__athApiExpectedQuery !== query || document.activeElement !== currentInput || currentInput?.value) return;
      currentInput.value = query;
      const clearButton = form.querySelector('[data-ath-search-clear]');
      if (clearButton) clearButton.hidden = false;
      currentInput.setSelectionRange?.(query.length, query.length);
    };

    window.requestAnimationFrame(restore);
    [80, 240, 500].forEach((delay) => window.setTimeout(restore, delay));
  };

  const protectHeaderInputValue = (form, input) => {
    if (input.__athApiValueProtected) return;
    const valueDescriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (!valueDescriptor?.get || !valueDescriptor?.set) return;

    Object.defineProperty(input, 'value', {
      configurable: true,
      get() {
        return valueDescriptor.get.call(this);
      },
      set(nextValue) {
        const isLegacyClear = nextValue === '' && form.__athApiExpectedQuery && document.activeElement === this;
        if (!isLegacyClear) valueDescriptor.set.call(this, nextValue);
      },
    });
    input.__athApiValueProtected = true;
  };

  const createProductImage = (item, className) => {
    const imageWrap = makeElement('span', className);
    if (item.image_link) {
      const image = document.createElement('img');
      image.src = item.image_link;
      image.alt = '';
      image.loading = 'lazy';
      imageWrap.appendChild(image);
    }
    return imageWrap;
  };

  const createAutocompleteProduct = (item) => {
    const link = makeElement('a', 'ath-api-search-panel__product');
    link.href = normalizeLink(item.link);
    link.setAttribute('data-ath-search-option', '');
    link.setAttribute('role', 'option');
    link.appendChild(createProductImage(item, 'ath-api-search-panel__image'));

    const details = makeElement('span', 'ath-api-search-panel__details');
    if (item.product_code) details.appendChild(makeElement('span', 'ath-api-search-panel__sku', `Item #${item.product_code}`));
    details.appendChild(makeElement('span', 'ath-api-search-panel__title', item.title || 'Product'));
    link.appendChild(details);
    link.appendChild(makeElement('span', 'ath-api-search-panel__price', formatMoney(item.price)));
    return link;
  };

  const closeNativeAutocomplete = () => {
    if (typeof window.Searchanise?.AutocompleteCloseAll === 'function') {
      window.Searchanise.AutocompleteCloseAll();
    }
    document.body.classList.remove('snize-instant-widget-is-open');
  };

  const suppressLegacyStickySearch = () => {
    document.querySelectorAll('.snize-sticky-searchbox, #snize-modal-sticky-searchbox').forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    });
    document.documentElement.classList.remove('snize-no-scroll');
    document.body?.classList.remove('snize-sticky-widget-opened', 'snize-no-scroll');
  };

  const usesLegacyMobileSearch = () => window.matchMedia('(max-width: 991px)').matches;

  const closeAutocomplete = (form) => {
    const panel = form?.querySelector('[data-ath-api-search-panel]');
    const input = form?.querySelector(headerSearchInputSelector);
    if (!panel || !input) return;
    panel.hidden = true;
    panel.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    form.classList.remove('is-search-open');
    if (!document.querySelector('.ath-site-header__search.is-search-open')) {
      document.body.classList.remove('ath-api-search-open');
    }
  };

  const closeAllAutocomplete = (except = null) => {
    document.querySelectorAll('.ath-site-header__search[data-ath-api-search-ready]').forEach((form) => {
      if (form !== except) closeAutocomplete(form);
    });
  };

  const openAutocomplete = (form) => {
    const panel = form.querySelector('[data-ath-api-search-panel]');
    const input = form.querySelector(headerSearchInputSelector);
    if (!panel || !input) return;
    closeAllAutocomplete(form);
    if (!usesLegacyMobileSearch()) closeNativeAutocomplete();
    if (window.matchMedia('(max-width: 749px)').matches) {
      const inputRect = input.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const viewportOffsetTop = window.visualViewport?.offsetTop || 0;
      const viewportBottom = viewportOffsetTop + viewportHeight;
      const panelTop = Math.max(viewportOffsetTop + 8, Math.min(viewportBottom - 120, inputRect.bottom + 8));
      const panelHeight = Math.max(120, viewportBottom - panelTop - 12);
      panel.style.setProperty('--ath-api-panel-top', `${panelTop}px`);
      panel.style.setProperty('--ath-api-panel-max-height', `${panelHeight}px`);
    }
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    form.classList.add('is-search-open');
    document.body.classList.add('ath-api-search-open');
  };

  const renderAutocompleteLoading = (form) => {
    const panel = form.querySelector('[data-ath-api-search-panel]');
    if (!panel) return;
    panel.replaceChildren();
    const status = makeElement('div', 'ath-api-search-panel__status', 'Searching…');
    status.setAttribute('role', 'status');
    panel.appendChild(status);
    openAutocomplete(form);
  };

  const renderAutocompleteError = (form) => {
    const panel = form.querySelector('[data-ath-api-search-panel]');
    if (!panel) return;
    panel.replaceChildren(makeElement('div', 'ath-api-search-panel__status', 'Search is temporarily unavailable. Press Enter to view the search page.'));
    openAutocomplete(form);
  };

  const renderAutocomplete = (form, query, response) => {
    const input = form.querySelector(headerSearchInputSelector);
    const panel = form.querySelector('[data-ath-api-search-panel]');
    if (!input || !panel) return;
    if (input.value.trim() !== query && form.__athApiExpectedQuery !== query) {
      closeAutocomplete(form);
      return;
    }

    panel.replaceChildren();
    const correctedQuery = response.correctedQuery || '';
    const items = rankItems(response.items || [], query, correctedQuery).slice(0, 6);

    if (correctedQuery && normalizeText(correctedQuery) !== normalizeText(query)) {
      const correction = makeElement('p', 'ath-api-search-panel__correction');
      correction.append('Showing results for ');
      correction.appendChild(makeElement('strong', '', correctedQuery));
      panel.appendChild(correction);
    }

    if (!items.length) {
      const empty = makeElement('div', 'ath-api-search-panel__empty');
      empty.appendChild(makeElement('strong', '', 'No products found'));
      empty.appendChild(makeElement('span', '', 'Try a shorter product number or a different spelling.'));
      panel.appendChild(empty);
    } else {
      const list = makeElement('div', 'ath-api-search-panel__list');
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Product suggestions');
      items.forEach((item) => list.appendChild(createAutocompleteProduct(item)));
      panel.appendChild(list);
    }

    const footer = makeElement('a', 'ath-api-search-panel__all');
    footer.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
    footer.setAttribute('data-ath-search-option', '');
    footer.textContent = response.totalItems
      ? `View all ${formatResultCount(response.totalItems)} for “${query}”`
      : `Search for “${query}”`;
    footer.appendChild(makeElement('span', '', '→'));
    panel.appendChild(footer);
    openAutocomplete(form);
    preserveHeaderInputValue(input, query);
  };

  const initializeAutocompleteForm = (form, index) => {
    const input = form.querySelector(headerSearchInputSelector);
    if (!input) return;
    if (form.dataset.athApiSearchReady === 'true' && form.__athApiSearchInput === input) return;

    form.dataset.athApiSearchReady = 'true';
    form.__athApiSearchInput = input;
    protectHeaderInputValue(form, input);
    let panel = form.querySelector('[data-ath-api-search-panel]');
    if (!panel) {
      panel = makeElement('div', 'ath-api-search-panel');
      panel.id = `AthApiSearchPanel-${index}-${Date.now()}`;
      panel.hidden = true;
      panel.dataset.athApiSearchPanel = '';
      panel.setAttribute('aria-live', 'polite');
      form.appendChild(panel);
    }
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');

    let requestToken = 0;
    const search = debounce(async () => {
      const query = input.value.trim();
      if (query.length < 2) {
        closeAutocomplete(form);
        return;
      }

      const token = ++requestToken;
      renderAutocompleteLoading(form);
      try {
        const response = await requestSearchWithBrandCorrection({ q: query, maxResults: 24, facets: 'false' });
        if (token === requestToken) renderAutocomplete(form, query, response);
      } catch (_error) {
        if (token !== requestToken) return;
        if (input.value.trim() === query) renderAutocompleteError(form);
        else closeAutocomplete(form);
      }
    });

    const handleInput = () => {
      const query = input.value.trim();
      const hasValue = query.length > 0;
      const clearButton = form.querySelector('[data-ath-search-clear]');
      if (clearButton) clearButton.hidden = !hasValue;
      form.__athApiExpectedQuery = query;
      if (query.length < 2) closeAutocomplete(form);
      else preserveHeaderInputValue(input, query);
      search();
    };
    const handleFocus = () => {
      if (input.value.trim().length >= 2) search();
    };
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        closeAutocomplete(form);
        return;
      }
      if (event.key === 'ArrowDown' && !panel.hidden) {
        const firstOption = panel.querySelector('[data-ath-search-option]');
        if (firstOption) {
          event.preventDefault();
          firstOption.focus();
        }
      }
    };
    input.__athApiSearchHandlers = {
      input: handleInput,
      focus: handleFocus,
      keydown: handleKeydown,
    };
    if (!form.__athApiFormListenersReady) {
      form.__athApiFormListenersReady = true;
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeAutocomplete(form);
          form.querySelector(headerSearchInputSelector)?.focus({ preventScroll: true });
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const options = [...panel.querySelectorAll('[data-ath-search-option]')];
        const currentIndex = options.indexOf(document.activeElement);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(options.length - 1, currentIndex + direction));
        if (options[nextIndex]) {
          event.preventDefault();
          options[nextIndex].focus();
        }
      });
      form.addEventListener('submit', (event) => {
        const currentInput = form.querySelector(headerSearchInputSelector);
        const query = currentInput?.value.trim() || '';
        closeAutocomplete(form);
        if (currentInput?.name === 'q' || query.length < 2) return;
        event.preventDefault();
        window.location.assign(`${searchUrl}?q=${encodeURIComponent(query)}`);
      });
      form.querySelector('[data-ath-search-clear]')?.addEventListener('click', () => {
        form.__athApiExpectedQuery = '';
        const currentInput = form.querySelector(headerSearchInputSelector);
        if (currentInput) currentInput.value = '';
        closeAutocomplete(form);
      });
    }

    if (form.__athApiExpectedQuery) preserveHeaderInputValue(input, form.__athApiExpectedQuery);
  };

  const initializeAutocomplete = () => {
    document.querySelectorAll('.ath-site-header__search').forEach((form, index) => initializeAutocompleteForm(form, index));
  };

  const detachLegacyAutocompleteListeners = () => {
    document.querySelectorAll('.ath-site-header__search').forEach((form, index) => {
      const input = form.querySelector(headerSearchInputSelector);
      if (!input) return;
      const isOwnedInput = input.dataset.athApiSearchOwned === 'true';
      const hasLegacySearchaniseClass = input.classList.contains('snize-input-style');
      if (isOwnedInput && !hasLegacySearchaniseClass) return;

      const cleanInput = input.cloneNode(true);
      const inputValue = input.value;
      const wasFocused = document.activeElement === input;
      const selectionStart = input.selectionStart;
      const selectionEnd = input.selectionEnd;
      cleanInput.type = 'text';
      cleanInput.name = 'ath_api_q';
      cleanInput.value = inputValue;
      cleanInput.classList.remove('snize-input-style');
      cleanInput.dataset.athApiSearchOwned = 'true';
      form.dataset.athApiLegacyDetached = 'true';
      form.__athApiSearchInput = null;
      input.replaceWith(cleanInput);
      initializeAutocompleteForm(form, index);
      if (wasFocused) {
        cleanInput.focus({ preventScroll: true });
        if (Number.isInteger(selectionStart) && Number.isInteger(selectionEnd)) {
          cleanInput.setSelectionRange?.(selectionStart, selectionEnd);
        }
      }
    });
  };

  const routeHeaderSearchEvent = (event) => {
    const input = event.target?.closest?.('.ath-site-header__search-input');
    if (!input) return;

    if (event.type === 'keydown' && event.key === 'Enter' && !event.isComposing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      navigateHeaderSearch(input.closest('.ath-site-header__search'));
      return;
    }

    if (!input.__athApiSearchHandlers) initializeAutocompleteForm(input.closest('.ath-site-header__search'), 0);
    const handlers = input.__athApiSearchHandlers;
    if (event.type === 'input') handlers?.input?.();
    else if (event.type === 'search') handlers?.input?.();
    else if (event.type === 'focus') handlers?.focus?.();
    else if (event.type === 'keydown') handlers?.keydown?.(event);

    event.stopImmediatePropagation();
  };

  const navigateHeaderSearch = (form) => {
    if (!form?.classList.contains('ath-site-header__search')) return false;
    const input = form.querySelector(headerSearchInputSelector);
    const query = (input?.value || form.__athApiExpectedQuery || '').trim();
    if (query.length < 2) {
      closeAutocomplete(form);
      return false;
    }

    closeAutocomplete(form);
    window.location.assign(`${searchUrl}?q=${encodeURIComponent(query)}`);
    return true;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target?.closest?.('.ath-site-header__search');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    navigateHeaderSearch(form);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target?.closest?.('.ath-site-header__search-button');
    const form = button?.closest?.('.ath-site-header__search');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    navigateHeaderSearch(form);
  }, true);

  const submitSearchPageForm = (form) => {
    const input = form?.querySelector('[data-ath-page-search-input]');
    const query = input?.value.trim() || '';
    if (query.length < 2) return false;

    if (typeof form.__athApiSubmitSearch === 'function') form.__athApiSubmitSearch();
    else window.location.assign(`${searchUrl}?q=${encodeURIComponent(query)}`);
    return true;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target?.closest?.('[data-ath-page-search-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitSearchPageForm(form);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target?.closest?.('[data-ath-page-search-form] button[type="submit"]');
    const form = button?.closest?.('[data-ath-page-search-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitSearchPageForm(form);
  }, true);

  ['beforeinput', 'input', 'search', 'focus', 'focusin', 'pointerdown', 'keydown', 'keypress', 'keyup']
    .forEach((eventName) => document.addEventListener(eventName, routeHeaderSearchEvent, true));

  const createProductCard = (item) => {
    const card = makeElement('article', 'ath-search-api__card');
    const imageLink = makeElement('a', 'ath-search-api__card-image');
    imageLink.href = normalizeLink(item.link);
    imageLink.appendChild(createProductImage(item, 'ath-search-api__card-image-inner'));
    card.appendChild(imageLink);

    const content = makeElement('div', 'ath-search-api__card-content');
    if (item.product_code) content.appendChild(makeElement('p', 'ath-search-api__sku', `Item #${item.product_code}`));
    const title = makeElement('h3', 'ath-search-api__card-title');
    const titleLink = makeElement('a', '', item.title || 'Product');
    titleLink.href = normalizeLink(item.link);
    title.appendChild(titleLink);
    content.appendChild(title);

    const priceRow = makeElement('div', 'ath-search-api__price-row');
    priceRow.appendChild(makeElement('span', 'ath-search-api__price', formatMoney(item.price)));
    if (Number(item.list_price) > Number(item.price)) {
      priceRow.appendChild(makeElement('s', 'ath-search-api__compare-price', formatMoney(item.list_price)));
    }
    content.appendChild(priceRow);
    const viewLink = makeElement('a', 'ath-search-api__view-link', 'View product ');
    viewLink.href = normalizeLink(item.link);
    viewLink.appendChild(makeElement('span', '', '→'));
    content.appendChild(viewLink);
    card.appendChild(content);
    return card;
  };

  const initializeSearchPage = () => {
    const root = document.querySelector('[data-ath-api-search-page]');
    if (!root || root.dataset.athApiReady === 'true') return;
    root.dataset.athApiReady = 'true';
    document.documentElement.classList.add('ath-api-search-page-active');

    const form = root.querySelector('[data-ath-page-search-form]');
    const input = root.querySelector('[data-ath-page-search-input]');
    const vendorSelect = root.querySelector('[data-ath-vendor-filter]');
    const sortSelect = root.querySelector('[data-ath-sort]');
    const count = root.querySelector('[data-ath-result-count]');
    const correction = root.querySelector('[data-ath-correction]');
    const grid = root.querySelector('[data-ath-results-grid]');
    const loadMore = root.querySelector('[data-ath-load-more]');
    const clearFilter = root.querySelector('[data-ath-clear-filter]');
    const status = root.querySelector('[data-ath-search-status]');
    const urlQuery = new URLSearchParams(window.location.search).get('q') || root.dataset.query || '';
    const state = {
      query: urlQuery.trim(),
      vendor: '',
      sort: 'relevance',
      limit: 36,
      visible: 12,
      response: null,
      vendorOptions: [],
      token: 0,
    };

    input.value = state.query;
    input.name = 'ath_api_page_q';

    const setLoading = () => {
      grid.replaceChildren();
      grid.setAttribute('aria-busy', 'true');
      status.textContent = 'Searching the product catalog…';
      count.textContent = 'Searching…';
      correction.hidden = true;
      loadMore.hidden = true;
      for (let index = 0; index < 8; index += 1) grid.appendChild(makeElement('div', 'ath-search-api__card ath-search-api__skeleton'));
    };

    const updateVendorOptions = (response) => {
      const vendorFacet = response.facets?.find((facet) => facet.attribute === 'vendor');
      if (!state.vendor && vendorFacet?.buckets?.length) state.vendorOptions = vendorFacet.buckets;

      const currentValue = state.vendor;
      vendorSelect.replaceChildren(new Option('All brands', ''));
      if (currentValue && !state.vendorOptions.some((option) => option.value === currentValue)) {
        vendorSelect.add(new Option(currentValue, currentValue));
      }
      state.vendorOptions.slice(0, 40).forEach((option) => {
        vendorSelect.add(new Option(`${option.value} (${option.count})`, option.value));
      });
      vendorSelect.value = currentValue;
      clearFilter.hidden = !currentValue;
    };

    const render = () => {
      const response = state.response || { totalItems: 0, items: [] };
      const correctedQuery = response.correctedQuery || '';
      const allItems = state.sort === 'relevance'
        ? rankItems(response.items || [], state.query, correctedQuery)
        : (response.items || []);
      const visibleItems = allItems.slice(0, state.visible);

      grid.replaceChildren();
      grid.setAttribute('aria-busy', 'false');
      count.textContent = `${formatResultCount(response.totalItems)} for “${state.query}”`;
      status.textContent = `${visibleItems.length} product${visibleItems.length === 1 ? '' : 's'} displayed.`;

      if (correctedQuery && normalizeText(correctedQuery) !== normalizeText(state.query)) {
        correction.replaceChildren();
        correction.append('Showing results for ');
        correction.appendChild(makeElement('strong', '', correctedQuery));
        correction.hidden = false;
      } else {
        correction.hidden = true;
      }

      updateVendorOptions(response);

      if (!visibleItems.length) {
        const empty = makeElement('div', 'ath-search-api__empty');
        empty.appendChild(makeElement('h3', '', 'No products found'));
        empty.appendChild(makeElement('p', '', 'Try a shorter product number, check the spelling, or search by product type.'));
        const retry = makeElement('button', 'ath-search-api__button', 'Search catheters');
        retry.type = 'button';
        retry.addEventListener('click', () => {
          input.value = 'catheter';
          state.query = 'catheter';
          state.vendor = '';
          runSearch();
        });
        empty.appendChild(retry);
        grid.appendChild(empty);
      } else {
        visibleItems.forEach((item) => grid.appendChild(createProductCard(item)));
      }

      const canShowMoreLoaded = state.visible < allItems.length;
      const canRequestMore = allItems.length < Number(response.totalItems || 0) && state.limit < 120;
      loadMore.hidden = !(canShowMoreLoaded || canRequestMore);
    };

    const renderError = () => {
      grid.replaceChildren();
      grid.setAttribute('aria-busy', 'false');
      count.textContent = 'Search unavailable';
      status.textContent = 'Search is temporarily unavailable.';
      const error = makeElement('div', 'ath-search-api__empty');
      error.appendChild(makeElement('h3', '', 'We couldn’t load the results'));
      error.appendChild(makeElement('p', '', 'Please try again in a moment or call us at 1-800-526-5895.'));
      const retry = makeElement('button', 'ath-search-api__button', 'Try again');
      retry.type = 'button';
      retry.addEventListener('click', () => runSearch());
      error.appendChild(retry);
      grid.appendChild(error);
    };

    const runSearch = async () => {
      state.query = input.value.trim();
      if (state.query.length < 2) return;
      const token = ++state.token;
      setLoading();

      const parameters = {
        q: state.query,
        maxResults: state.limit,
        facets: 'true',
        sortBy: state.sort === 'relevance' ? 'relevance' : state.sort.startsWith('price') ? 'price' : 'title',
        sortOrder: state.sort.endsWith('desc') ? 'desc' : 'asc',
      };
      if (state.vendor) parameters['restrictBy[vendor]'] = state.vendor;

      try {
        const response = await requestSearchWithBrandCorrection(parameters);
        if (token !== state.token) return;
        state.response = response;
        render();
      } catch (_error) {
        if (token === state.token) renderError();
      }
    };

    const submitSearch = () => {
      const nextQuery = input.value.trim();
      if (nextQuery.length < 2) return;
      state.query = nextQuery;
      state.vendor = '';
      state.limit = 36;
      state.visible = 12;
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('q', nextQuery);
      window.history.replaceState({}, '', nextUrl);
      runSearch();
    };
    form.__athApiSubmitSearch = submitSearch;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitSearch();
    });
    vendorSelect.addEventListener('change', () => {
      state.vendor = vendorSelect.value;
      state.visible = 12;
      state.limit = 36;
      runSearch();
    });
    sortSelect.addEventListener('change', () => {
      state.sort = sortSelect.value;
      state.visible = 12;
      runSearch();
    });
    clearFilter.addEventListener('click', () => {
      state.vendor = '';
      vendorSelect.value = '';
      state.visible = 12;
      runSearch();
    });
    loadMore.addEventListener('click', () => {
      const availableItems = state.response?.items?.length || 0;
      if (state.visible < availableItems) {
        state.visible += 12;
        render();
      } else {
        state.limit = Math.min(120, state.limit + 36);
        state.visible += 12;
        runSearch();
      }
    });

    if (state.query.length >= 2) runSearch();
  };

  document.addEventListener('pointerdown', (event) => {
    if (!event.target.closest('.ath-site-header__search')) closeAllAutocomplete();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllAutocomplete();
  });
  window.addEventListener('resize', debounce(() => {
    closeAllAutocomplete();
    if (!usesLegacyMobileSearch()) closeNativeAutocomplete();
  }, 120));

  const initialize = () => {
    suppressLegacyStickySearch();
    initializeAutocomplete();
    initializeSearchPage();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();

  const finishLegacyAutocompleteDetachment = () => {
    detachLegacyAutocompleteListeners();
    [600, 1600, 3200].forEach((delay) => window.setTimeout(detachLegacyAutocompleteListeners, delay));
  };

  if (document.readyState === 'complete') finishLegacyAutocompleteDetachment();
  else window.addEventListener('load', finishLegacyAutocompleteDetachment, { once: true });

  new MutationObserver(initialize).observe(document.documentElement, { childList: true, subtree: true });
})();
